import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Maximize2, X, Camera } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// Import valid carousel assets from /src/assets/carousel and /src/assets
import BabyHyena from '../assets/BabyHyena.webp';
import MonkeyDark from '../assets/MonkeyDark.webp';
import MultiColouredBird from '../assets/MultiColouredBird.webp';
import imgLion from '../assets/BlackWhiteLion.webp';
import imgTree from '../assets/Tree.webp';
import imgCheetahs from '../assets/GalleryCheetahs.webp';
import imgDeer from '../assets/GalleryDeer.webp';
import imgGiraffe from '../assets/GalleryGiraffe.webp';
import imgHyenas from '../assets/GalleryHyenas.webp';
import imgSunset from '../assets/GallerySunset.webp';
import imgWildDogs from '../assets/GalleryWildDogs.webp';
import imgBlueBird from '../assets/GalleryBlueBird.webp';
import imgElephants from '../assets/GalleryElephants.webp';
import imgElephantBabies from '../assets/GalleryElphantBabies.webp';
import imgRedBird from '../assets/GalleryRedBird.webp';

interface CarouselSlide {
	image: string;
	title: {
		en: string;
		it: string;
		fr: string;
		de: string;
	};
}

const CAROUSEL_SLIDES: CarouselSlide[] = [
	{
		image: MonkeyDark,
		title: {
			en: 'Monkey looking through the trees in black and white',
			it: 'Scimmia che guarda attraverso gli alberi in bianco e nero',
			fr: 'Singe observant à travers les arbres en noir et blanc',
			de: 'Affe blickt durch die Bäume in Schwarz-Weiß',
		},
	},
	{
		image: MultiColouredBird,
		title: {
			en: 'The African Pygmy Kingfisher',
			it: 'Il Martin Pescatore Pigmeo Africano',
			fr: 'Le Martin-pêcheur Pygmée d’Afrique',
			de: 'Der Afrika-Zwergfischer',
		},
	},
	{
		image: imgCheetahs,
		title: {
			en: 'Cheetahs in the Wild',
			it: 'Ghepardi nella Natura Selvaggia',
			fr: 'Guépards dans la Nature Sauvage',
			de: 'Geparden in freier Wildbahn',
		},
	},
	{
		image: imgBlueBird,
		title: {
			en: 'Blue Bird in the Savannah',
			it: 'Uccello Blu nella Savana',
			fr: 'Oiseau Bleu dans la Savane',
			de: 'Blauer Vogel in der Savanne',
		},
	},
	{
		image: imgSunset,
		title: {
			en: 'Sunset over the Savannah',
			it: 'Tramonto sulla Savana',
			fr: 'Coucher de Soleil sur la Savane',
			de: 'Sonnenuntergang über der Savanne',
		},
	},
	{
		image: BabyHyena,
		title: {
			en: 'Baby Hyenas',
			it: 'Cuccioli di Iena',
			fr: 'Bébés Hyènes',
			de: 'Hyänen-Babys',
		},
	},
	{
		image: imgRedBird,
		title: {
			en: 'Red Bird in the Savannah',
			it: 'Uccello Rosso nella Savana',
			fr: 'Oiseau Rouge dans la Savane',
			de: 'Roter Vogel in der Savanne',
		},
	},
	{
		image: imgGiraffe,
		title: {
			en: 'Giraffe in the Savanna',
			it: 'Giraffa nella Savana',
			fr: 'Girafe dans la Savane',
			de: 'Giraffe in der Savanne',
		},
	},
	{
		image: imgWildDogs,
		title: {
			en: 'Wild Dogs on the Hunt',
			it: 'Licaoni durante la Caccia',
			fr: 'Lycaons à la Chasse',
			de: 'Wildhunde auf der Jagd',
		},
	},
	{
		image: imgElephantBabies,
		title: {
			en: 'Elephant Babies in the Wild',
			it: 'Cuccioli di Elefante nella Natura',
			fr: 'Bébés Éléphants dans la Nature',
			de: 'Elefanten-Babys in freier Wildbahn',
		},
	},
	{
		image: imgTree,
		title: {
			en: 'Silhouette of the Acacia Sun',
			it: 'Silhouette dell’Acacia al Tramonto',
			fr: 'Silhouette de l’Acacia au Soleil Couchant',
			de: 'Akazien-Silhouette im goldenen Abendlicht',
		},
	},
	{
		image: imgLion,
		title: {
			en: 'The Sovereign Pride: African Lion',
			it: 'Il Sovrano della Savana: Leone Africano',
			fr: 'Le Souverain de la Savane : Lion d’Afrique',
			de: 'Der Herrscher der Savanne: Afrikanischer Löwe',
		},
	},
	{
		image: imgElephants,
		title: {
			en: 'Elephants in the Wild',
			it: 'Elefanti nella Natura Selvaggia',
			fr: 'Éléphants dans la Nature Sauvage',
			de: 'Elefanten in freier Wildbahn',
		},
	},
	{
		image: imgHyenas,
		title: {
			en: 'Hyenas in the Savannah',
			it: 'Iene nella Savana',
			fr: 'Hyènes dans la Savane',
			de: 'Hyänen in der Savanne',
		},
	},
	{
		image: imgDeer,
		title: {
			en: 'Deer in the Forest',
			it: 'Cervi nella Foresta',
			fr: 'Cerfs dans la Forêt',
			de: 'Hirsche im Wald',
		},
	},
];

export default function Carousel() {
	const { currentLanguage } = useTranslation();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [touchStart, setTouchStart] = useState<number | null>(null);
	const [touchEnd, setTouchEnd] = useState<number | null>(null);

	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const SLIDE_DURATION = 5500; // 5.5 seconds per slide

	const nextSlide = useCallback(() => {
		setCurrentIndex(prev => (prev + 1) % CAROUSEL_SLIDES.length);
	}, []);

	const prevSlide = useCallback(() => {
		setCurrentIndex(prev => (prev - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length);
	}, []);

	const goToSlide = (index: number) => {
		setCurrentIndex(index);
	};

	// Auto-play timer
	useEffect(() => {
		if (isPlaying && !isLightboxOpen) {
			timerRef.current = setInterval(() => {
				nextSlide();
			}, SLIDE_DURATION);
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, [isPlaying, isLightboxOpen, nextSlide]);

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft') {
				prevSlide();
			} else if (e.key === 'ArrowRight') {
				nextSlide();
			} else if (e.key === 'Escape' && isLightboxOpen) {
				setIsLightboxOpen(false);
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [nextSlide, prevSlide, isLightboxOpen]);

	// Touch handlers for mobile swipe
	const handleTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null);
		setTouchStart(e.targetTouches[0].clientX);
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};

	const handleTouchEnd = () => {
		if (!touchStart || !touchEnd) return;
		const distance = touchStart - touchEnd;
		const isLeftSwipe = distance > 50;
		const isRightSwipe = distance < -50;

		if (isLeftSwipe) {
			nextSlide();
		} else if (isRightSwipe) {
			prevSlide();
		}
	};

	const activeSlide = CAROUSEL_SLIDES[currentIndex];
	const lang = currentLanguage in activeSlide.title ? currentLanguage : 'en';

	return (
		<section
			id='safari-visual-carousel'
			className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'
			aria-label='Safari Visual Highlights Carousel'
		>
			{/* Main Carousel Container */}
			<div
				className='relative rounded-2xl md:rounded-3xl overflow-hidden bg-brand-forest-dark border border-earth-sand/30 shadow-2xl group'
				onMouseEnter={() => setIsPlaying(false)}
				onMouseLeave={() => setIsPlaying(true)}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
			>
				{/* Image Display Area */}
				<div className='relative w-full aspect-4/3 sm:aspect-16/10 md:aspect-21/9 min-h-90 md:min-h-115 overflow-hidden'>
					{CAROUSEL_SLIDES.map((slide, index) => {
						const isActive = index === currentIndex;
						return (
							<div
								key={index}
								className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
									isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
								}`}
								aria-hidden={!isActive}
							>
								<img
									src={slide.image}
									alt={slide.title[lang as keyof typeof slide.title] || slide.title.en}
									className={`w-full h-full object-cover object-center transform transition-transform duration-3000 ease-out ${
										isActive ? 'scale-105' : 'scale-100'
									}`}
									referrerPolicy='no-referrer'
									loading={index === 0 ? 'eager' : 'lazy'}
									decoding='async'
								/>
								{/* Subtle vignette for controls */}
								<div className='absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-black/30 pointer-events-none' />
							</div>
						);
					})}

					{/* Top Utility Bar (Badge + Play/Pause + Expand) */}
					<div className='absolute top-4 sm:top-6 inset-x-4 sm:inset-x-8 z-20 flex items-center justify-between pointer-events-auto'>
						<div className='inline-flex items-center space-x-2 bg-brand-forest-dark/70 backdrop-blur-md border border-brand-gold/30 rounded-full px-3.5 py-1.5 shadow-sm'>
							<Camera className='w-3.5 h-3.5 text-brand-gold' />
							<span className='font-sans text-[10px] sm:text-xs tracking-[0.2em] text-brand-gold-light uppercase font-bold'>
								Safari Gallery
							</span>
						</div>

						<div className='flex items-center space-x-2'>
							<button
								onClick={() => setIsPlaying(prev => !prev)}
								className='p-2 sm:p-2.5 rounded-full bg-brand-forest-dark/70 hover:bg-brand-forest/90 text-earth-cream border border-earth-sand/30 backdrop-blur-md transition-colors cursor-pointer'
								title={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
								aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
							>
								{isPlaying ? (
									<Pause className='w-4 h-4 text-brand-gold' />
								) : (
									<Play className='w-4 h-4 text-brand-gold' />
								)}
							</button>

							<button
								onClick={() => setIsLightboxOpen(true)}
								className='p-2 sm:p-2.5 rounded-full bg-brand-forest-dark/70 hover:bg-brand-forest/90 text-earth-cream border border-earth-sand/30 backdrop-blur-md transition-colors cursor-pointer'
								title='Open full screen view'
								aria-label='Open full screen view'
							>
								<Maximize2 className='w-4 h-4 text-brand-gold' />
							</button>
						</div>
					</div>

					{/* Navigation Arrow Left */}
					<button
						onClick={prevSlide}
						className='absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3.5 rounded-full bg-brand-forest-dark/65 hover:bg-brand-forest-dark/90 text-earth-cream border border-brand-gold/40 hover:border-brand-gold backdrop-blur-md transition-all duration-200 transform hover:scale-105 active:scale-95 cursor-pointer shadow-lg'
						aria-label='Previous slide'
					>
						<ChevronLeft className='w-5 h-5 sm:w-6 sm:h-6 text-brand-gold' />
					</button>

					{/* Navigation Arrow Right */}
					<button
						onClick={nextSlide}
						className='absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3.5 rounded-full bg-brand-forest-dark/65 hover:bg-brand-forest-dark/90 text-earth-cream border border-brand-gold/40 hover:border-brand-gold backdrop-blur-md transition-all duration-200 transform hover:scale-105 active:scale-95 cursor-pointer shadow-lg'
						aria-label='Next slide'
					>
						<ChevronRight className='w-5 h-5 sm:w-6 sm:h-6 text-brand-gold' />
					</button>

					{/* Bottom Slide Counter & Dots Overlay (Clean, no text) */}
					<div className='absolute bottom-4 sm:bottom-6 inset-x-0 z-20 flex justify-center pointer-events-auto'>
						<div className='inline-flex items-center space-x-3 bg-brand-forest-dark/80 backdrop-blur-md border border-brand-gold/30 rounded-full px-4 py-2 shadow-lg'>
							<span className='font-mono text-xs text-brand-gold font-bold tracking-widest'>
								{String(currentIndex + 1).padStart(2, '0')} <span className='text-earth-sand/50'>/</span>{' '}
								{String(CAROUSEL_SLIDES.length).padStart(2, '0')}
							</span>

							<div className='w-px h-3 bg-earth-sand/30' />

							<div className='flex items-center space-x-1.5'>
								{CAROUSEL_SLIDES.map((_, idx) => (
									<button
										key={idx}
										onClick={() => goToSlide(idx)}
										className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
											idx === currentIndex
												? 'w-5 sm:w-6 bg-brand-gold'
												: 'w-1.5 bg-earth-sand/40 hover:bg-earth-sand/70'
										}`}
										aria-label={`Go to slide ${idx + 1}`}
									/>
								))}
							</div>
						</div>
					</div>
				</div>

				{/* Interactive Thumbnail Strip */}
				<div className='bg-brand-forest-dark/95 border-t border-earth-sand/20 px-4 py-3 sm:px-6 overflow-x-auto scrollbar-none'>
					<div className='flex items-center space-x-2.5 sm:space-x-3 min-w-max justify-start md:justify-center'>
						{CAROUSEL_SLIDES.map((slide, idx) => {
							const isSelected = idx === currentIndex;
							return (
								<button
									key={idx}
									onClick={() => goToSlide(idx)}
									className={`relative rounded-lg overflow-hidden transition-all duration-200 cursor-pointer h-12 w-16 sm:h-14 sm:w-20 shrink-0 border-2 ${
										isSelected
											? 'border-brand-gold ring-2 ring-brand-gold/40 scale-105 shadow-md'
											: 'border-transparent opacity-60 hover:opacity-100 hover:scale-102'
									}`}
									aria-label={`Preview slide ${idx + 1}: ${slide.title.en}`}
								>
									<img
										src={slide.image}
										alt={slide.title.en}
										loading='lazy'
										decoding='async'
										className='w-full h-full object-cover'
										referrerPolicy='no-referrer'
									/>
									{isSelected && <div className='absolute inset-0 bg-brand-gold/10' />}
								</button>
							);
						})}
					</div>
				</div>
			</div>

			{/* Fullscreen Lightbox Modal */}
			{isLightboxOpen && (
				<div
					id='carousel-lightbox-modal'
					className='fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col justify-between p-4 sm:p-6 animate-fade-in'
				>
					{/* Header bar */}
					<div className='flex items-center justify-between text-white z-10'>
						<div className='inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-3.5 py-1'>
							<span className='font-mono text-xs text-brand-gold font-bold tracking-widest'>
								{String(currentIndex + 1).padStart(2, '0')} / {String(CAROUSEL_SLIDES.length).padStart(2, '0')}
							</span>
						</div>

						<button
							onClick={() => setIsLightboxOpen(false)}
							className='p-2 rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors'
							aria-label='Close lightbox'
						>
							<X className='w-6 h-6' />
						</button>
					</div>

					{/* Main Image in Lightbox */}
					<div className='relative flex-1 flex items-center justify-center p-2 sm:p-6 max-h-[85vh]'>
						<img
							src={activeSlide.image}
							alt='Safari Highlight'
							loading='lazy'
							decoding='async'
							className='max-h-full max-w-full object-contain rounded-xl shadow-2xl'
							referrerPolicy='no-referrer'
						/>

						{/* Left Lightbox Arrow */}
						<button
							onClick={prevSlide}
							className='absolute left-2 sm:left-6 p-3 rounded-full bg-black/50 hover:bg-black/80 text-white border border-white/20 cursor-pointer transition-transform active:scale-95'
							aria-label='Previous slide'
						>
							<ChevronLeft className='w-6 h-6 text-brand-gold' />
						</button>

						{/* Right Lightbox Arrow */}
						<button
							onClick={nextSlide}
							className='absolute right-2 sm:right-6 p-3 rounded-full bg-black/50 hover:bg-black/80 text-white border border-white/20 cursor-pointer transition-transform active:scale-95'
							aria-label='Next slide'
						>
							<ChevronRight className='w-6 h-6 text-brand-gold' />
						</button>
					</div>
				</div>
			)}
		</section>
	);
}
